import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadsApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Select, SelectContent, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import UnitSelectItems from './UnitSelectItems';
import {
  FileText, Upload, X, Loader2, AlertCircle, Trash2, CheckCircle2, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/utils';

/**
 * BrokerDocImportDialog — upload rate con / BOL PDFs, review parsed loads
 * before committing. Uses the async job flow so a long-running Gemini parse
 * doesn't hit the ingress timeout.
 *
 * Props:
 *  - open, onOpenChange — dialog control (parent-owned).
 *  - weekStart — pay week the imported loads land in.
 *  - units — list from `unitsApi.getAll()`.
 *  - onImported — callback after successful commit (refresh loads list).
 */
export default function BrokerDocImportDialog({ open, onOpenChange, weekStart, units, onImported }) {
  const [files, setFiles] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [phase, setPhase] = useState('pick');            // pick | parsing | review | committing
  const [progress, setProgress] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [overrides, setOverrides] = useState({});        // load_ref -> { unit_id, rate_amount }
  const [error, setError] = useState('');
  const [crossWeekDlgOpen, setCrossWeekDlgOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // Per-row "Import anyway" — user opts to include duplicates that would otherwise be skipped
  const [forceIncluded, setForceIncluded] = useState({}); // { LOAD_REF: true }
  const pollRef = useRef(null);

  // Reset when the dialog closes
  useEffect(() => {
    if (!open) {
      setFiles([]); setJobId(null); setPhase('pick'); setProgress(null);
      setParseResult(null); setOverrides({}); setError(''); setCrossWeekDlgOpen(false);
      setForceIncluded({});
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [open]);

  const onFilesPicked = (list) => {
    const arr = Array.from(list || []).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (arr.length === 0 && list && list.length > 0) {
      toast.error('Only PDF files are supported');
    }
    setFiles((prev) => [...prev, ...arr]);
  };

  const onDragOver = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) onFilesPicked(e.dataTransfer.files);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const startParse = async () => {
    if (files.length === 0) return toast.error('Add at least one PDF');
    setError(''); setPhase('parsing'); setProgress({ phase: 'starting' });
    try {
      const r = await loadsApi.parseDocuments(files, weekStart);
      const id = r.data.job_id;
      setJobId(id);
      // Poll every 3s until done
      pollRef.current = setInterval(async () => {
        try {
          const s = await loadsApi.parseStatus(id);
          if (s.data.status === 'done') {
            clearInterval(pollRef.current); pollRef.current = null;
            const result = s.data.result;
            setParseResult(result);
            // Seed overrides with the extracted amount so the user can just tweak
            const seed = {};
            (result.parsed_loads || []).forEach((l) => {
              seed[l.load_reference] = { unit_id: '', rate_amount: l.rate_amount ?? '' };
            });
            setOverrides(seed);
            setPhase('review');
          } else {
            setProgress(s.data.progress || {});
          }
        } catch {
          clearInterval(pollRef.current); pollRef.current = null;
          setError('Lost the parse job. Please try again.');
          setPhase('pick');
        }
      }, 3000);
    } catch (e) {
      setError(apiErrorMessage(e, 'Parse failed'));
      setPhase('pick');
    }
  };

  const updateOverride = (ref, key, value) => {
    setOverrides((prev) => ({ ...prev, [ref]: { ...(prev[ref] || {}), [key]: value } }));
  };

  const validLoads = useCallback(() => {
    const rows = parseResult?.parsed_loads || [];
    return rows.filter((l) => {
      const o = overrides[l.load_reference] || {};
      const rate = o.rate_amount === '' ? l.rate_amount : Number(o.rate_amount);
      return rate !== null && rate !== undefined && !Number.isNaN(rate) && Number(rate) > 0;
    });
  }, [parseResult, overrides]);

  const commit = async (opts = {}) => {
    if (!parseResult) return;
    // Only rows that WILL be imported need a valid rate:
    // - Non-duplicates: always imported
    // - Same-week / cross-week duplicates: imported only if the user forced them
    const isForced = (l) => !!forceIncluded[l.load_reference];
    const willImport = (parseResult.parsed_loads || []).filter((l) => {
      if (l.skip_reason === 'already_this_week') return isForced(l);
      if (l.skip_reason === 'cross_week_duplicate') return isForced(l) || !!opts.forceCrossWeek;
      return true;
    });
    const missing = willImport.filter((l) => {
      const o = overrides[l.load_reference] || {};
      const rate = o.rate_amount === '' ? l.rate_amount : Number(o.rate_amount);
      return !(rate && Number(rate) > 0);
    });
    if (missing.length > 0 && !opts.skipMissingRates) {
      return toast.error(`Please enter a rate for: ${missing.slice(0, 3).map(m => m.load_reference).join(', ')}${missing.length > 3 ? ` (+${missing.length - 3} more)` : ''}`);
    }
    // Cross-week duplicates still gate the commit — but ONLY the ones the user hasn't explicitly forced
    const unforcedCrossWeek = (parseResult.cross_week_duplicates || [])
      .filter((d) => !isForced({ load_reference: d.load_reference }));
    if (unforcedCrossWeek.length > 0 && !opts.forceCrossWeek && !crossWeekDlgOpen) {
      setCrossWeekDlgOpen(true);
      return;
    }

    setPhase('committing');
    try {
      // Build the payload — merge overrides into each parsed load
      const rows = willImport
        .filter((l) => {
          const o = overrides[l.load_reference] || {};
          const rate = o.rate_amount === '' ? l.rate_amount : Number(o.rate_amount);
          return rate && Number(rate) > 0;
        })
        .map((l) => ({ ...l, rate_amount: Number((overrides[l.load_reference] || {}).rate_amount || l.rate_amount) }));

      const overridesPayload = {};
      rows.forEach((l) => {
        overridesPayload[l.load_reference] = {
          unit_id: (overrides[l.load_reference] || {}).unit_id || null,
          rate_amount: l.rate_amount,
        };
      });

      const skipRefs = opts.skipRefs || [];
      // Per-row "Import anyway" set → bypass same-week AND cross-week skip logic
      const forceRefs = Object.keys(forceIncluded).filter((k) => forceIncluded[k]);
      const r = await loadsApi.commitDocuments({
        weekStart, parsedLoads: rows, overrides: overridesPayload,
        forceCrossWeek: !!opts.forceCrossWeek, skipRefs, forceRefs,
      });
      toast.success(`Imported ${r.data.created_count} load${r.data.created_count === 1 ? '' : 's'}`);
      onImported?.();
      onOpenChange(false);
    } catch (e) {
      setError(apiErrorMessage(e, 'Import failed'));
      setPhase('review');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[880px] w-[96vw] max-h-[88vh] p-0 flex flex-col"
          data-testid="broker-docs-import-dialog"
          onDragOver={phase === 'pick' ? onDragOver : undefined}
          onDragLeave={phase === 'pick' ? onDragLeave : undefined}
          onDrop={phase === 'pick' ? onDrop : undefined}
        >
          <DialogHeader className="p-4 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" /> Import Loads from Broker Documents
            </DialogTitle>
            <DialogDescription className="text-xs">
              Drop one or more Rate Confirmations or BOL PDFs. We&apos;ll pull out the load details automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            {/* PICK ------------------------------------------------------ */}
            {phase === 'pick' && (
              <>
                <label
                  htmlFor="broker-doc-input"
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    dragActive
                      ? 'border-blue-600 bg-blue-100/70 ring-2 ring-blue-300'
                      : 'border-blue-300 bg-blue-50/40 hover:bg-blue-50'
                  }`}
                  data-testid="broker-doc-drop"
                >
                  <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? 'text-blue-700' : 'text-blue-500'}`} />
                  <div className="text-sm font-medium text-slate-700">
                    {dragActive ? 'Drop to add files' : 'Drop PDF files here or click to browse'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Rate confirmations, BOLs, or combined documents · one or many.</div>
                  <input
                    id="broker-doc-input"
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ''; }}
                    data-testid="broker-doc-input"
                  />
                </label>

                {files.length > 0 && (
                  <div className="rounded-lg border divide-y">
                    {files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="p-2 flex items-center gap-2 text-xs" data-testid={`broker-doc-file-${i}`}>
                        <FileText className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <span className="text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`broker-doc-remove-${i}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {error && (
                  <div className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {error}
                  </div>
                )}
              </>
            )}

            {/* PARSING --------------------------------------------------- */}
            {phase === 'parsing' && (
              <div className="text-center py-12 space-y-3">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-600" />
                <div className="text-sm font-medium text-slate-700">Uploading your documents…</div>
                <div className="text-xs text-muted-foreground">
                  {progress?.phase === 'extracting' && progress.files_total
                    ? `Reading file ${progress.files_done} of ${progress.files_total}`
                    : progress?.phase === 'merging'
                      ? 'Checking for duplicates across weeks…'
                      : 'This can take 30–90 seconds for large PDFs.'}
                </div>
              </div>
            )}

            {/* REVIEW ---------------------------------------------------- */}
            {phase === 'review' && parseResult && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm">
                    <span className="font-semibold text-slate-800">{parseResult.loads_count}</span>
                    <span className="text-muted-foreground"> load{parseResult.loads_count === 1 ? '' : 's'} found</span>
                    {parseResult.same_week_duplicates_count > 0 && (
                      <Badge variant="outline" className="ml-2 border-blue-300 text-blue-700 bg-blue-50 text-[10px]" data-testid="doc-same-week-badge">
                        {parseResult.same_week_duplicates_count} same-week duplicate{parseResult.same_week_duplicates_count === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {parseResult.cross_week_duplicates_count > 0 && (
                      <Badge variant="outline" className="ml-2 border-blue-300 text-blue-700 bg-blue-50 text-[10px]" data-testid="doc-cross-week-badge">
                        {parseResult.cross_week_duplicates_count} cross-week duplicate{parseResult.cross_week_duplicates_count === 1 ? '' : 's'}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Duplicates are skipped by default — flip the row toggle to import anyway.
                  </div>
                </div>

                {(parseResult.parsed_loads || []).length === 0 ? (
                  <div className="text-center py-12 text-sm text-muted-foreground">
                    No loads detected in the uploaded document(s).
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded border">
                    <Table className="min-w-[900px] text-xs">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Load ID / BOL</TableHead>
                          <TableHead>Broker</TableHead>
                          <TableHead>Route</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead className="w-[130px]">Rate</TableHead>
                          <TableHead className="w-[190px]">Unit</TableHead>
                          <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parseResult.parsed_loads.map((l) => {
                          const ov = overrides[l.load_reference] || {};
                          const rateVal = ov.rate_amount === '' ? '' : (ov.rate_amount ?? l.rate_amount ?? '');
                          const needsRate = !rateVal || Number(rateVal) <= 0;
                          const isCrossWeek = l.skip_reason === 'cross_week_duplicate';
                          const isSameWeek = l.skip_reason === 'already_this_week';
                          const isDup = isCrossWeek || isSameWeek;
                          const forced = !!forceIncluded[l.load_reference];
                          // Dim only when it's a duplicate AND the user has NOT opted to include it
                          const dim = isDup && !forced;
                          const existing = l.existing;
                          const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
                          return (
                            <React.Fragment key={l.load_reference}>
                            <TableRow
                              className={dim ? 'opacity-60' : ''}
                              data-testid={`doc-load-row-${l.load_reference}`}
                            >
                              <TableCell className="font-mono">
                                <div className="font-semibold">{l.load_reference}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {l.document_type === 'combined' ? 'RC + BOL' : (l.document_type === 'bol' ? 'BOL' : 'Rate Con')}
                                  {l.pages?.length ? ` · pages ${l.pages.join(', ')}` : ''}
                                </div>
                                {isCrossWeek && (
                                  <Badge variant="outline" className="mt-1 border-blue-300 text-blue-700 bg-blue-50 text-[9px] px-1 py-0" data-testid={`doc-dup-badge-${l.load_reference}`}>
                                    duplicate · other week
                                  </Badge>
                                )}
                                {isSameWeek && (
                                  <Badge variant="outline" className="mt-1 border-blue-300 text-blue-700 bg-blue-50 text-[9px] px-1 py-0" data-testid={`doc-dup-badge-${l.load_reference}`}>
                                    duplicate · this week
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="font-medium truncate max-w-[140px]">{l.broker_name}</div>
                                {l.broker_contact_phone && (
                                  <div className="text-[10px] text-muted-foreground truncate">{l.broker_contact_phone}</div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="truncate max-w-[180px]">{l.pickup_city || l.pickup_location || '—'} → {l.delivery_city || l.delivery_location || '—'}</div>
                                <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">
                                  {l.miles ? `${l.miles} mi · ` : ''}{l.trailer_type || ''}
                                </div>
                              </TableCell>
                              <TableCell className="text-[11px] whitespace-nowrap">
                                <div>{l.pickup_date || '—'}</div>
                                <div className="text-muted-foreground">↓ {l.delivery_date || '—'}</div>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="Enter"
                                  value={rateVal}
                                  onChange={(e) => updateOverride(l.load_reference, 'rate_amount', e.target.value)}
                                  className={`h-7 text-xs ${needsRate ? 'border-amber-400 bg-amber-50/50' : ''}`}
                                  data-testid={`doc-rate-input-${l.load_reference}`}
                                />
                                {needsRate && (
                                  <div className="text-[9px] text-amber-600 mt-0.5">Rate required</div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={ov.unit_id || ''}
                                  onValueChange={(v) => updateOverride(l.load_reference, 'unit_id', v)}
                                >
                                  <SelectTrigger className="h-7 text-xs" data-testid={`doc-unit-select-${l.load_reference}`}>
                                    <SelectValue placeholder="Assign later" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <UnitSelectItems units={units} hideInactive includeCompany />
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setParseResult((prev) => ({
                                      ...prev,
                                      parsed_loads: prev.parsed_loads.filter((x) => x.load_reference !== l.load_reference),
                                      loads_count: (prev.loads_count || 0) - 1,
                                    }));
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                  title="Remove this row"
                                  data-testid={`doc-drop-row-${l.load_reference}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </TableCell>
                            </TableRow>
                            {/* Duplicate comparison + per-row action — friendly blue site theme */}
                            {isDup && (
                              <TableRow className="bg-blue-50/50 border-b">
                                <TableCell colSpan={7} className="py-2">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap px-2">
                                    <AlertCircle className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                                    <div className="text-[11px]">
                                      <div className="font-semibold text-blue-800">
                                        {isSameWeek
                                          ? 'Heads up — this load is already in the current week.'
                                          : 'Heads up — this load is already saved in another week.'}
                                      </div>
                                      {existing && (
                                        <div className="text-slate-700 mt-0.5">
                                          Existing → <b>{existing.week_start}</b>
                                          {existing.broker_name ? <> · {existing.broker_name}</> : null}
                                          {existing.payout_amount != null ? <> · {money(existing.payout_amount)}</> : null}
                                        </div>
                                      )}
                                    </div>
                                    <div className="sm:ml-auto flex items-center gap-2 whitespace-nowrap">
                                      <span className={`text-[11px] ${forced ? 'text-blue-700 font-semibold' : 'text-slate-500'}`}>
                                        {forced ? 'Will import anyway' : 'Will skip'}
                                      </span>
                                      <Switch
                                        checked={forced}
                                        onCheckedChange={(v) => setForceIncluded((prev) => ({ ...prev, [l.load_reference]: v }))}
                                        data-testid={`doc-dup-force-${l.load_reference}`}
                                      />
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {parseResult.file_reports?.some((r) => r.error) && (
                  <div className="rounded bg-red-50 border border-red-200 p-2 text-xs text-red-700 space-y-0.5">
                    {parseResult.file_reports.filter(r => r.error).map((r, i) => (
                      <div key={i}>⚠ {r.filename}: {r.error}</div>
                    ))}
                  </div>
                )}

                {error && (
                  <div className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {error}
                  </div>
                )}
              </>
            )}

            {/* COMMITTING ------------------------------------------------ */}
            {phase === 'committing' && (
              <div className="text-center py-12 space-y-3">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-emerald-600" />
                <div className="text-sm font-medium text-slate-700">Creating loads…</div>
              </div>
            )}
          </div>

          <DialogFooter className="p-3 border-t flex-wrap gap-2">
            {phase === 'pick' && (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="broker-doc-cancel">Cancel</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={files.length === 0}
                  onClick={startParse}
                  data-testid="broker-doc-parse"
                >
                  <FileText className="h-3.5 w-3.5 mr-1" />
                  Parse {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : ''}
                </Button>
              </>
            )}
            {phase === 'review' && (
              <>
                <Button variant="outline" onClick={() => { setPhase('pick'); setFiles([]); setParseResult(null); }} data-testid="broker-doc-back">
                  Add more files
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={validLoads().length === 0}
                  onClick={() => commit()}
                  data-testid="broker-doc-commit"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  Import {validLoads().length} load{validLoads().length === 1 ? '' : 's'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cross-week duplicate confirm — themed BLUE */}
      <Dialog open={crossWeekDlgOpen} onOpenChange={setCrossWeekDlgOpen}>
        <DialogContent className="sm:max-w-[620px] w-[94vw] max-h-[80vh] p-0 flex flex-col" data-testid="broker-doc-cross-week-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <AlertCircle className="h-4 w-4" /> Duplicate Load IDs Detected
            </DialogTitle>
            <DialogDescription className="text-sm">
              These loads already exist in another week. Skip them, or add them anyway?
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 divide-y divide-blue-100">
              {(parseResult?.cross_week_duplicates || []).map((d) => (
                <div key={d.load_reference} className="p-3 text-xs">
                  <div className="font-semibold text-slate-800 mb-1">Load {d.load_reference}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded bg-white/70 border border-slate-200 p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Already in system</div>
                      <div>Week: <b>{d.existing?.week_start || '—'}</b></div>
                      <div>Broker: <b>{d.existing?.broker_name || '—'}</b></div>
                    </div>
                    <div className="rounded bg-blue-100/70 border border-blue-200 p-2">
                      <div className="text-[10px] uppercase text-blue-700">Being imported</div>
                      <div>Week: <b>{d.incoming?.week_start || '—'}</b></div>
                      <div>Amount: <b>${Number(d.incoming?.payout_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}</b></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="p-3 border-t gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setCrossWeekDlgOpen(false)} data-testid="broker-doc-dup-cancel">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => {
                const skipRefs = (parseResult?.cross_week_duplicates || []).map(d => d.load_reference);
                setCrossWeekDlgOpen(false);
                commit({ skipRefs });
              }}
              data-testid="broker-doc-dup-skip"
            >
              Skip all duplicates
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => { setCrossWeekDlgOpen(false); commit({ forceCrossWeek: true }); }}
              data-testid="broker-doc-dup-add"
            >
              Add all anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
